/* Response Status codes allow layers in the data stack to return nuanced information about
  the data requested. Like HTTP, status codes, they let a layer distinguish between
  "I can't fulfill your request now" and "I can't fulfill your request ever.

  Don't confuse these with BibleOffline.OfflineState, which tells us the location of data;
  these distinguish results of a request.

  The pattern for request methods is to return [data, status] for any get() type methods.

  Where possible, we borrow HTTP Status codes. Exceptions:

  - We 300 to mean `LATER`, i.e. "Not Found Now" (instead of using a timeout type error) because the
    intention is like a "redirect through time" instead of through (name) space.
*/
export type Status = number; // Regarding the entity you requested...
// Most Gettables only need be concerned with these common codes.
export const SUCCESS: Status = 200; // here it is
export const NOT_FOUND: Status = 404; // I don't have it (see comment above for why this isn't 404)
export const FAILURE: Status = 500; // rare: you will probably get an exception instead of this, but should handle it

// Connectable data sources may want to add these nuances
export const BAD_REQUEST: Status = 400; // I don't understand what you're asking for
export const LATER: Status = 300; // I can't get it now, but probably can when I have a better connection
export const NEVER: Status = 410; // I know about it, but will never have it
export const AUTHN_PROBLEM: Status = 401; // fix your credentials
export const AUTHZ_PROBLEM: Status = 403; // you don't have permission to have it

// Caching stores may want to add these nuances
export const CACHED: Status = 201; // here it is, fresh from the server. and I made a local copy
export const LOCAL: Status = 203; // here it is, from my (possibly stale) local copy
export const QUEUED: Status = 302; // I can't get it now, but will try grab a local copy soon as I can

const knownStatuses = [
  // common statuses
  SUCCESS,
  NOT_FOUND,
  FAILURE,
  // online nuances
  BAD_REQUEST,
  LATER,
  NEVER,
  AUTHN_PROBLEM,
  AUTHZ_PROBLEM,
  // caching nuances
  CACHED,
  LOCAL,
  QUEUED
];

export function statusType(c: Status) {
  return Math.floor(c / 100) * 100;
}

export function success(c: Status) {
  return statusType(c) === 200;
}

export function local(c: Status) {
  return [LOCAL, CACHED].indexOf(c) >= 0;
}

export function retry(c: Status) {
  return statusType(c) === 300;
}

export function failure(c: Status) {
  return statusType(c) > 300;
}

export function statusFromHttp(httpStatus: number): Status {
  const s = Math.floor(httpStatus) as Status;

  // if we recognize the status return it
  if (knownStatuses.indexOf(s) > -1) return s;

  // otherwise, round down to the nearest 100
  return statusType(s) as Status;
}
